import type { RouteStop } from '../models/RouteStop';
import { applyBankerSkip, canSkipRouteStop } from './routeStops';
import { normalizeRouteStop } from '../models/RouteStop';

export type SkipRequestStatus = 'pending';

export function hasPendingSkipRequest(stop: Pick<RouteStop, 'skipRequestStatus'>): boolean {
  return String(stop.skipRequestStatus ?? '') === 'pending';
}

/** Runner may request skip during collection when no request is already pending. */
export function canRequestSkip(stop: RouteStop): boolean {
  if (stop.kind === 'house') return false;
  return canSkipRouteStop(stop) && !hasPendingSkipRequest(stop);
}

/** Banker/god may approve or reject a pending runner skip request. */
export function canReviewSkipRequest(stop: RouteStop): boolean {
  return hasPendingSkipRequest(stop) && stop.status !== 'cleared' && stop.status !== 'skipped';
}

export function applySkipRequest(
  stop: RouteStop,
  reason: string,
  requestedBy: string,
  atMs: number = Date.now(),
): RouteStop {
  return normalizeRouteStop({
    ...stop,
    skipRequestStatus: 'pending',
    skipRequestReason: reason.trim() || 'unavailable',
    skipRequestedBy: requestedBy,
    skipRequestedAt: atMs,
  });
}

export function applySkipRequestReject(
  stop: RouteStop,
  reviewedBy: string,
  atMs: number = Date.now(),
): RouteStop {
  return normalizeRouteStop({
    ...stop,
    skipRequestStatus: null,
    skipRequestReason: null,
    skipRequestedBy: null,
    skipRequestedAt: null,
    skipReviewedBy: reviewedBy,
    skipReviewedAt: atMs,
  });
}

/** Immediate skip (banker) or approved request — clears pending request fields. */
export function applyAuthorizedSkip(
  stop: RouteStop,
  reason: string,
  skippedBy: string,
): RouteStop {
  const base = applyBankerSkip(stop, reason, skippedBy);
  return normalizeRouteStop({
    ...base,
    skipRequestStatus: null,
    skipRequestReason: null,
    skipRequestedBy: null,
    skipRequestedAt: null,
    skipReviewedBy: skippedBy,
    skipReviewedAt: Date.now(),
  });
}
